/*
 * Copyright (C) 2020 Graylog, Inc.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the Server Side Public License, version 1,
 * as published by MongoDB, Inc.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * Server Side Public License for more details.
 *
 * You should have received a copy of the Server Side Public License
 * along with this program. If not, see
 * <http://www.mongodb.com/licensing/server-side-public-license>.
 */
import React, { useMemo } from 'react';

import Popover from 'components/common/Popover';
import { keySeparator } from 'views/Constants';
import OnClickPopoverValueGroups from 'views/components/visualizations/OnClickPopover/OnClickPopoverValueGroups';
import formatValueWithUnitLabel from 'views/components/visualizations/utils/formatValueWithUnitLabel';
import { getPrettifiedValue } from 'views/components/visualizations/utils/unitConverters';
import getHoverSwatchColor from 'views/components/visualizations/utils/getHoverSwatchColor';
import type { ValueGroups, OnClickPopoverDropdownProps } from 'views/components/visualizations/OnClickPopover/Types';

const PieOnClickPopoverDropdown = ({ clickPoint, config, setFieldData }: OnClickPopoverDropdownProps) => {
  const { rowPivotValues, columnPivotValues, metricValue } = useMemo<ValueGroups>(() => {
    if (!clickPoint || !config) return {};

    const traceColor = getHoverSwatchColor(clickPoint);
    const splitNames: Array<string | number> = (clickPoint.data.originalName ?? clickPoint.data.name).split(
      keySeparator,
    );
    const metric: string = config.series.length === 1 ? config.series[0].function : (splitNames.pop() as string);
    const columnValues = splitNames.filter((value) => value !== metric);

    const columnPivotsToFields = config?.columnPivots?.flatMap((pivot) => pivot.fields) ?? [];

    const rowPivotsToFields = config?.rowPivots?.flatMap((pivot) => pivot.fields) ?? [];
    const splitXValues: Array<string | number> = rowPivotsToFields?.length
      ? clickPoint.data.originalLabels?.[clickPoint.pointNumber].split(keySeparator)
      : [];

    return {
      rowPivotValues: splitXValues.map((value, i) => ({
        value,
        field: rowPivotsToFields[i],
        text: String(value),
        traceColor,
      })),
      columnPivotValues: columnValues.map((value, i) => ({
        value,
        field: columnPivotsToFields[i],
        text: String(value),
        traceColor,
      })),
      metricValue: {
        value: clickPoint.value,
        field: metric,
        text: `${String(clickPoint?.value)}`,
        traceColor,
      },
    };
  }, [clickPoint, config]);

  const formatedPercentageValue = useMemo(() => {
    if (!clickPoint.percent) return clickPoint.text;

    const { value, unit } = getPrettifiedValue(clickPoint.percent, { abbrev: 'd%', unitType: 'percent' });

    return formatValueWithUnitLabel(value, unit?.abbrev);
  }, [clickPoint.percent, clickPoint.text]);

  return (
    <Popover.Dropdown title={formatedPercentageValue}>
      <OnClickPopoverValueGroups
        rowPivotValues={rowPivotValues}
        columnPivotValues={columnPivotValues}
        metricValue={metricValue}
        setFieldData={setFieldData}
        config={config}
      />
    </Popover.Dropdown>
  );
};

export default PieOnClickPopoverDropdown;
